import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { MatIcon } from "@angular/material/icon";
import { catchError, forkJoin, of } from "rxjs";
import {
  DashboardGraphItem,
  DashboardKpi,
  GRAPH_COLORS,
  RangoPeriodo,
  buildDonutStyle,
  formatMinutes,
  getMonthSortKey,
  obtenerRangoPeriodo,
  porcentaje,
  textoAFecha,
  toNumber,
} from "src/app/dashboard/shared/dashboard-graficas";
import { PeriodoDashboardComponent } from "src/app/dashboard/shared/periodo-dashboard/periodo-dashboard.component";
import { PestanaDashboard } from "src/app/dashboard/shared/pestana-dashboard";
import { DenunciasService } from "src/app/denuncias/denuncias.service";
import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { Denuncia, EstatusDenuncia } from "src/app/shared/model/denuncias/denuncia.model";
import { DenunciaDashboard, DenunciaGrafica } from "src/app/shared/model/denuncias/denuncia-dashboard.model";

/** Denuncias recientes que se listan en la tabla. */
const RECIENTES = 5;
/** Meses que se pintan en la tendencia (los ultimos del periodo). */
const MESES_TENDENCIA = 12;
/** Categorias que se pintan en las barras. */
const CATEGORIAS_TOP = 8;
/** Umbral por omision del backend, por si la respuesta no lo trae. */
const DIAS_SIN_ATENDER = 5;

/**
 * Pestaña "Denuncias" del Dashboard de Inicio: version compacta del
 * dashboard propio (/DENUNCIAS/dashboard). Periodo, KPIs, dona por
 * estatus, barras por categoria, tendencia mensual y las denuncias
 * recientes. Consulta al montarse; el cascaron (dashini) la vuelve a
 * consultar con actualizar().
 *
 * El modulo es GLOBAL (la tabla denuncia no tiene tenant): las cifras son
 * de toda la instalacion, y por eso el endpoint exige sesion Y hoja de
 * menu del modulo, no solo el gateo del cascaron.
 *
 * La tabla no PINTA hechos ni datos del denunciante: solo codigo, fecha,
 * categoria, estatus y tipo. Ojo: consultarTabla.do SI devuelve la
 * descripcion completa en `hechos` (selectResumen), asi que el dato viaja
 * por el cable aunque no se dibuje. Quitarlo de verdad exige un
 * selectResumen sin `descripcion` en el DAO.
 */
@Component({
  selector: "app-dashboard-denuncias",
  standalone: true,
  templateUrl: "./dashboard-denuncias.component.html",
  styleUrls: ["../../shared/dashboard-tarjetas.css"],
  imports: [CommonModule, MatIcon, PeriodoDashboardComponent],
})
export class DashboardDenunciasInicioComponent implements OnInit, PestanaDashboard {
  readonly periodoInicial = "30";

  errorCarga = "";
  kpis: DashboardKpi[] = [];
  estatusItems: DashboardGraphItem[] = [];
  categoriaItems: DashboardGraphItem[] = [];
  mesItems: DashboardGraphItem[] = [];
  denunciasRecientes: Denuncia[] = [];
  totalDenuncias = 0;
  mesMax = 0;
  resumen: DenunciaDashboard = new DenunciaDashboard();
  /** A donde lleva "Ver todos": dashboard propio, kanban, o nada si no tiene hoja. */
  rutaVerTodos = "";

  private rango: RangoPeriodo = { inicio: "", fin: "" };
  private cargandoDatos = false;
  private readonly destroyRef = inject(DestroyRef);
  /** true desde que la pestaña se destruye; guarda el repintado (ver repintar()). */
  private destruido = false;

  private readonly statusColors: Record<number, string> = {
    [EstatusDenuncia.RECIBIDA]: "#d32f2f",
    [EstatusDenuncia.EN_INVESTIGACION]: "#1565c0",
    [EstatusDenuncia.CERRADA]: "#00897b",
  };

  constructor(
    private denunciasService: DenunciasService,
    private authService: AuthService,
    private router: Router,
    // La vista NO se repinta sola cuando llega la respuesta HTTP: en este
    // arranque zone.js no queda parcheando XMLHttpRequest, asi que el
    // estado cambia y el DOM se queda en "Actualizando indicadores..."
    // hasta el siguiente clic. Se pide el repintado a mano, el mismo
    // patron que usa el resto de la casa (asistencia/mi-asistencia).
    // NO quitar por "innecesario".
    private cdr: ChangeDetectorRef,
  ) {
    // Se registra ANTES de cualquier suscripcion: los callbacks de
    // DestroyRef corren en orden de registro, asi que cuando
    // takeUntilDestroyed cierre el stream al destruir la pestaña (y con
    // ello dispare su complete) esto ya vale true.
    this.destroyRef.onDestroy(() => (this.destruido = true));
  }

  ngOnInit() {
    const menus = this.authService.getMenuList();
    if (tieneAccesoMenu(menus, "DENUNCIAS/dashboard")) {
      this.rutaVerTodos = "/DENUNCIAS/dashboard";
    } else if (tieneAccesoMenu(menus, "DENUNCIAS/kanban")) {
      this.rutaVerTodos = "/DENUNCIAS/kanban";
    }
    this.rango = obtenerRangoPeriodo(this.periodoInicial);
    this.consultarDashboard();
  }

  get cargando(): boolean {
    return this.cargandoDatos;
  }

  actualizar(): void {
    this.consultarDashboard();
  }

  onPeriodo(rango: RangoPeriodo): void {
    this.rango = rango;
    this.consultarDashboard();
  }

  /** "dd/MM/yyyy - dd/MM/yyyy" del rango en curso, para la esquina de la barra. */
  get etiquetaPeriodo(): string {
    const inicio = this.fechaCorta(this.rango.inicio);
    const fin = this.fechaCorta(this.rango.fin);
    if (inicio && fin) {
      return `${inicio} - ${fin}`;
    }
    return inicio ? `Desde ${inicio}` : fin ? `Hasta ${fin}` : "Historico";
  }

  consultarDashboard(): void {
    this.cargandoDatos = true;
    this.errorCarga = "";

    forkJoin({
      resumen: this.denunciasService.consultarDashboard(this.crearReq()).pipe(catchError(() => of(null as DenunciaDashboard | null))),
      tabla: this.denunciasService.consultarDenunciasTabla(this.crearReqTabla()).pipe(catchError(() => of(null as ConsultaList<Denuncia> | null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const resumenOk = !!res.resumen && res.resumen.pCodRetorno !== "1";
          const tablaOk = !!res.tabla && res.tabla.pCodRetorno !== "1";

          this.resumen = resumenOk ? res.resumen : new DenunciaDashboard();
          this.totalDenuncias = toNumber(this.resumen.totalDenuncias);
          this.estatusItems = this.construirEstatus(this.resumen);
          this.categoriaItems = this.normalizarGraficas(this.resumen.porCategoria || []).slice(0, CATEGORIAS_TOP);
          this.mesItems = this.normalizarGraficas(this.resumen.porMes || [], false).slice(-MESES_TENDENCIA);
          this.mesMax = Math.max(...this.mesItems.map((item) => item.valor), 0);
          this.denunciasRecientes = tablaOk ? (res.tabla.list || []).slice(0, RECIENTES) : [];
          this.kpis = this.construirKpis(this.resumen);

          if (!resumenOk) {
            this.errorCarga = tablaOk
              ? "No fue posible cargar los indicadores de denuncias."
              : "No fue posible cargar la informacion de denuncias.";
          }
          this.repintar();
        },
        error: (e) => {
          console.error("Error al cargar dashboard de denuncias:", e);
          this.errorCarga = "No fue posible cargar la informacion de denuncias.";
          this.cargandoDatos = false;
          this.repintar();
        },
        complete: () => {
          this.cargandoDatos = false;
          this.repintar();
        },
      });
  }

  /**
   * Repinta la pestaña. detectChanges() sobre una vista ya destruida
   * truena, y el complete SI llega al destruir (takeUntilDestroyed cierra
   * el stream desde el propio onDestroy), de ahi el guardia.
   */
  private repintar(): void {
    if (this.destruido) {
      return;
    }
    // markForCheck ADEMAS de detectChanges: detectChanges refresca solo
    // ESTA vista, y el cascaron (dashini) lee el `cargando` de la pestaña
    // para habilitar su boton "Actualizar". Sin marcar el camino hacia la
    // raiz ese enlace se queda con el valor viejo: el boton se queda
    // deshabilitado para siempre y en desarrollo sale un NG0100.
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  getBarPercent(item: DashboardGraphItem, total = this.totalDenuncias): number {
    return porcentaje(item.valor, total);
  }

  getBarStyle(item: DashboardGraphItem, index: number, total = this.totalDenuncias): Record<string, string> {
    return {
      width: `${this.getBarPercent(item, total)}%`,
      background: item.color || GRAPH_COLORS[index % GRAPH_COLORS.length],
    };
  }

  getDonutStyle(): Record<string, string> {
    const conValor = this.estatusItems.filter((item) => item.valor > 0);
    if (this.totalDenuncias <= 0 || conValor.length === 0) {
      return { background: "#eef1f5" };
    }

    return buildDonutStyle(conValor, this.totalDenuncias);
  }

  getEstatusLabel(denuncia: Denuncia): string {
    switch (Number(denuncia?.id_estatus)) {
      case EstatusDenuncia.RECIBIDA:
        return "RECIBIDA";
      case EstatusDenuncia.EN_INVESTIGACION:
        return "EN INVESTIGACION";
      case EstatusDenuncia.CERRADA:
        return "CERRADA";
      default:
        return String(denuncia?.nombreEstatus || "SIN ESTATUS").toUpperCase();
    }
  }

  /** Reusa los chips de tickets: recibida roja, investigacion azul, cerrada verde. */
  getEstatusClass(denuncia: Denuncia): string {
    switch (Number(denuncia?.id_estatus)) {
      case EstatusDenuncia.RECIBIDA:
        return "ticket-status--abierta";
      case EstatusDenuncia.EN_INVESTIGACION:
        return "ticket-status--proceso";
      case EstatusDenuncia.CERRADA:
        return "ticket-status--atendida";
      default:
        return "ticket-status--sin";
    }
  }

  getTipoLabel(denuncia: Denuncia): string {
    const tipo = String(denuncia?.tipoDenuncia || "").toUpperCase();
    if (tipo === "ANONIMA") {
      return "Anonima";
    }
    return tipo === "CONFIDENCIAL" ? "Confidencial" : "N/D";
  }

  get sinAsignar(): number {
    return toNumber(this.resumen.sinAsignar);
  }

  get sinAtender(): number {
    return toNumber(this.resumen.sinAtender);
  }

  get diasSinAtender(): number {
    return toNumber(this.resumen.diasSinAtender) || DIAS_SIN_ATENDER;
  }

  get anonimas(): number {
    return toNumber(this.resumen.anonimas);
  }

  get confidenciales(): number {
    return toNumber(this.resumen.confidenciales);
  }

  abrirDenuncias(): void {
    if (this.rutaVerTodos) {
      this.router.navigate([this.rutaVerTodos]);
    }
  }

  abrirDenuncia(denuncia: Denuncia): void {
    if (denuncia?.id_denuncia) {
      this.router.navigate(["/DENUNCIAS/detalle", denuncia.id_denuncia]);
    }
  }

  /** fecha_ini a las 00:00 y fecha_fin a las 23:59:59 locales, como el dashboard propio. */
  private crearReq(): ConsultaList<Denuncia> {
    const req = new ConsultaList<Denuncia>();
    const inicio = this.rango.inicio ? textoAFecha(this.rango.inicio) : null;
    const fin = this.rango.fin ? textoAFecha(this.rango.fin) : null;
    if (inicio) {
      req.fecha_ini = inicio;
    }
    if (fin) {
      fin.setHours(23, 59, 59, 999);
      req.fecha_fin = fin;
    }
    return req;
  }

  private crearReqTabla(): ConsultaList<Denuncia> {
    const req = this.crearReq();
    req.regInicial = 0;
    req.regFinal = RECIENTES;
    return req;
  }

  /** Los tres estatus fijos del canal, con su total del periodo y su color. */
  private construirEstatus(resumen: DenunciaDashboard): DashboardGraphItem[] {
    return [
      { nombre: "Recibida", valor: toNumber(resumen.totalRecibidas), color: this.statusColors[EstatusDenuncia.RECIBIDA] },
      { nombre: "En investigacion", valor: toNumber(resumen.totalEnInvestigacion), color: this.statusColors[EstatusDenuncia.EN_INVESTIGACION] },
      { nombre: "Cerrada", valor: toNumber(resumen.totalCerradas), color: this.statusColors[EstatusDenuncia.CERRADA] },
    ];
  }

  /**
   * {nombre,total} del backend a DashboardGraphItem. Por valor de mayor a
   * menor, o cronologico cuando el nombre es un mes "MM-yy".
   */
  private normalizarGraficas(items: DenunciaGrafica[], ordenarPorValor = true): DashboardGraphItem[] {
    const normalizados = (items || [])
      .filter((item) => item && item.nombre !== null && item.nombre !== undefined)
      .map((item, index) => ({
        nombre: String(item.nombre || "Sin clasificar"),
        valor: toNumber(item.total),
        color: GRAPH_COLORS[index % GRAPH_COLORS.length],
      }));

    if (ordenarPorValor) {
      return normalizados.sort((a, b) => b.valor - a.valor);
    }

    return normalizados.sort((a, b) => getMonthSortKey(a.nombre) - getMonthSortKey(b.nombre));
  }

  private construirKpis(resumen: DenunciaDashboard): DashboardKpi[] {
    const recibidas = toNumber(resumen.totalRecibidas);
    const investigacion = toNumber(resumen.totalEnInvestigacion);
    const total = toNumber(resumen.totalDenuncias);

    return [
      {
        label: "Recibidas en el periodo",
        value: total,
        meta: this.etiquetaPeriodo,
        icon: "campaign",
        className: "ticket-kpi--total",
      },
      {
        label: "Casos activos",
        value: recibidas + investigacion,
        meta: `${recibidas} recibidas / ${investigacion} en investigacion`,
        icon: "pending_actions",
        className: "ticket-kpi--active",
      },
      {
        label: "Sin asignar",
        value: this.sinAsignar,
        // Foto del canal completo: este numero y su meta NO respetan el periodo.
        meta: `Foto actual: ${this.sinAtender} sin atender > ${this.diasSinAtender} dias`,
        icon: "assignment_late",
        className: "ticket-kpi--time",
      },
      {
        label: "Tiempo medio de cierre",
        value: this.formatearCierre(resumen.tiempoMedioCierreHoras),
        meta: resumen.tiempoMedioCierreHoras === null || resumen.tiempoMedioCierreHoras === undefined
          ? "Aun no hay denuncias cerradas"
          : "De registro a cierre, historico",
        icon: "timer",
        className: "ticket-kpi--done",
      },
      {
        label: "Anonimas",
        value: `${porcentaje(this.anonimas, total)}%`,
        meta: `${this.anonimas} anonimas / ${this.confidenciales} confidenciales`,
        icon: "visibility_off",
        className: "ticket-kpi--muted",
      },
    ];
  }

  /** Horas a "N h M min" cuando es menos de dos dias; si no, "N.N dias". */
  private formatearCierre(horas: number | null | undefined): string {
    if (horas === null || horas === undefined) {
      return "Sin cierres";
    }
    const valor = toNumber(horas);
    if (valor < 48) {
      return formatMinutes(valor * 60);
    }
    const dias = valor / 24;
    return `${Number.isInteger(dias) ? dias : dias.toFixed(1)} dias`;
  }

  private fechaCorta(texto: string): string {
    const fecha = texto ? textoAFecha(texto) : null;
    if (!fecha) {
      return "";
    }
    const dia = `${fecha.getDate()}`.padStart(2, "0");
    const mes = `${fecha.getMonth() + 1}`.padStart(2, "0");
    return `${dia}/${mes}/${fecha.getFullYear()}`;
  }
}
